import { useState } from 'react';
import { EyeOff, Eye } from 'lucide-react';

import { useBlacklist } from './useBlacklist';
import type { ComicCard, ComicDetails } from '@/sources/types';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

export interface BlacklistWarningProps {
  comic: ComicCard | ComicDetails;
  /** Children are rendered once the user opts in. If omitted, only the banner is shown. */
  children?: React.ReactNode;
  /** Render the full gallery by default, but show a dismissible banner. */
  alwaysShowChildren?: boolean;
  className?: string;
}

/**
 * Gallery-detail-page banner. Renders when a comic has any tag that matches
 * the blacklist. The user can "View anyway" to unhide. If `alwaysShowChildren`
 * is true, children are visible from the start but the banner stays as a
 * dismissable warning.
 */
export function BlacklistWarning({
  comic,
  children,
  alwaysShowChildren = false,
  className,
}: BlacklistWarningProps) {
  const { matchedTags, loading } = useBlacklist();
  const [revealed, setRevealed] = useState(alwaysShowChildren);
  const [dismissed, setDismissed] = useState(false);

  if (loading) {
    return alwaysShowChildren ? <>{children}</> : null;
  }

  const matched = matchedTags(comic);
  if (matched.length === 0) {
    return <>{children}</>;
  }

  const banner = !dismissed ? (
    <div
      role="alert"
      className={cn(
        'flex flex-col gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm',
        className,
      )}
    >
      <div className="flex items-start gap-2">
        <EyeOff className="mt-0.5 h-4 w-4 shrink-0" />
        <div className="flex-1">
          <p className="font-semibold">This gallery has blocked tags</p>
          <p className="mt-0.5 flex flex-wrap items-center gap-1 text-xs">
            <span>Matches:</span>
            {matched.map((t) => (
              <Badge key={t} variant="outline" className="text-[10px]">
                {t}
              </Badge>
            ))}
          </p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 pl-6">
        {!revealed && (
          <Button size="sm" variant="outline" onClick={() => setRevealed(true)}>
            <Eye className="mr-1 h-3.5 w-3.5" />
            View anyway
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={() => setDismissed(true)}>
          Dismiss
        </Button>
      </div>
    </div>
  ) : null;

  return (
    <>
      {banner}
      {revealed && children}
    </>
  );
}
